/**
 * The joints in the map's own walls.
 *
 * The compiler owns every surface you can touch and emits them as the boxes
 * they are: a wall is one slab from floor to ceiling. That is correct, and it
 * is also why an interior reads as a greybox — a real wall has a base, a head
 * and a division at intervals, and it is those, not the material, that tell
 * you a wall is a wall.
 *
 * What this used to do was give it a plinth, a coping and pilasters, or eight
 * horizontal ribs, on every wall in the level. Applied to every wall, an
 * architectural language stops being a language: the ribs meant "profiled
 * steel cladding" once and "shelving, everywhere, forever" four hundred times,
 * and they were most of what made a room read as procedurally decorated rather
 * than designed.
 *
 * The vocabulary now is the shadow gap. A dark reveal where the wall meets the
 * floor, another under the head, and one vertical joint per structural bay on
 * anything long enough to have bays. Three thin dark lines instead of eight
 * pale mouldings: it describes the same wall, it costs a fifth of the
 * geometry, and because the lines are graphite rather than tinted stone they
 * belong to the same small family as every other made thing in the world.
 *
 * None of it is collidable and none of it moves a surface. What the map is
 * remains the compiler's; this is only what it looks like.
 */

import * as THREE from 'three';
import type { CompiledLevel, Solid } from 'levelspec';

/** Roles that are a wall for trimming purposes. */
const WALLS = new Set(['static_hard', 'exterior_wall']);

function rng(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h ^= h << 13;
    h >>>= 0;
    h ^= h >> 17;
    h ^= h << 5;
    h >>>= 0;
    return h / 4294967296;
  };
}

export function buildDressing(
  level: CompiledLevel,
  seed: string,
  facade: 'punched' | 'banded' | 'industrial' = 'punched',
  night = false,
  marked: (s: Solid) => boolean = () => false,
  dim = false,
): THREE.Group {
  const group = new THREE.Group();
  const rand = rng(seed + ':trim');

  const pos: number[] = [];
  const norm: number[] = [];
  const col: number[] = [];
  const idx: number[] = [];
  const ePos: number[] = [];
  const eNorm: number[] = [];
  const eCol: number[] = [];
  const eIdx: number[] = [];

  /*
   * One colour, and it is a shadow.
   *
   * A reveal is not a moulding painted a different shade of the wall — it is
   * the dark you see into when two planes do not quite meet. Graphite, the
   * same graphite as every door and machine, so the eye files all of it under
   * "the made parts" without being told.
   */
  const reveal = new THREE.Color(0x151a20);
  /** The verticals: the shell's own colour, half a shade down so they read. */
  const pier = new THREE.Color(0xd2d8dd);
  /*
   * And the cove, which is a light and not a surface.
   *
   * Near-neutral on purpose: it is about to be the largest bright thing in a
   * night frame, and a tinted one would spend the whole `tinted` budget and
   * some of `accent hues` on a light fitting.
   */
  const cove = new THREE.Color(0xe8eef2);
  /** Paint on hardstanding: worn, not fresh, so it is a marking and not a light. */
  const paint = new THREE.Color(0xb9bfc4);
  /** The post a lamp stands on, which is a made thing like every other. */
  const post = new THREE.Color(0x151a20);
  /** The lamp itself, and the ground under it. */
  const lamplight = new THREE.Color(0xe4e9ed);
  const pool = new THREE.Color(0x8d9399);

  /** One axis-aligned box, in Three space, as six quads, into a given buffer. */
  const into = (
    P: number[], N: number[], C: number[], I: number[],
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
    c: THREE.Color,
  ): void => {
    const faces: [number[], number[]][] = [
      [[x0, y1, z0, x1, y1, z0, x1, y1, z1, x0, y1, z1], [0, 1, 0]],
      [[x0, y0, z1, x1, y0, z1, x1, y0, z0, x0, y0, z0], [0, -1, 0]],
      [[x0, y0, z1, x1, y0, z1, x1, y1, z1, x0, y1, z1], [0, 0, 1]],
      [[x1, y0, z0, x0, y0, z0, x0, y1, z0, x1, y1, z0], [0, 0, -1]],
      [[x1, y0, z1, x1, y0, z0, x1, y1, z0, x1, y1, z1], [1, 0, 0]],
      [[x0, y0, z0, x0, y0, z1, x0, y1, z1, x0, y1, z0], [-1, 0, 0]],
    ];
    for (const [v, n] of faces) {
      const start = P.length / 3;
      for (let k = 0; k < 4; k++) {
        P.push(v[k * 3], v[k * 3 + 1], v[k * 3 + 2]);
        N.push(n[0], n[1], n[2]);
        C.push(c.r, c.g, c.b);
      }
      // Wound from the outward normal, the same test the scenery uses.
      const e1 = [v[3] - v[0], v[4] - v[1], v[5] - v[2]];
      const e2 = [v[6] - v[0], v[7] - v[1], v[8] - v[2]];
      const cr = [
        e1[1] * e2[2] - e1[2] * e2[1],
        e1[2] * e2[0] - e1[0] * e2[2],
        e1[0] * e2[1] - e1[1] * e2[0],
      ];
      if (cr[0] * n[0] + cr[1] * n[1] + cr[2] * n[2] >= 0)
        I.push(start, start + 1, start + 2, start, start + 2, start + 3);
      else I.push(start, start + 2, start + 1, start, start + 3, start + 2);
    }
  };
  const box = (
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
    c: THREE.Color,
  ): void => into(pos, norm, col, idx, x0, y0, z0, x1, y1, z1, c);
  /** The same box, but as its own light. */
  const lamp = (
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
    c: THREE.Color,
  ): void => into(ePos, eNorm, eCol, eIdx, x0, y0, z0, x1, y1, z1, c);

  let trimmed = 0;
  /*
   * The floors this wall touches, so the skirting can sit on them.
   *
   * A wall solid runs from its own bottom, which on a map with any change of
   * level is not the floor beside it — the band was drawn at the wall's base
   * and on the high side of a step it is buried under the ground, so the
   * skirting vanishes for that stretch and comes back where the floor drops.
   * That is the hole at the corner: not a gap in the run, a band underneath
   * the floor.
   *
   * So the wall is asked which floors it actually stands next to, and gets a
   * band at each of them. Two levels meeting at a wall get two bands, which is
   * what a room with a step in it looks like.
   */
  const floors = (level.solids as Solid[]).filter((f) => f.role === 'floor');
  const levelsBeside = (s: Solid): number[] => {
    const [x0, y0, z0] = s.box.min;
    const [x1, y1, z1] = s.box.max;
    const out = new Set<number>();
    for (const f of floors) {
      // Overlapping in plan, once the wall is grown by a little more than its
      // own thickness so the floor on either side counts.
      const grow = 0.6;
      if (f.box.max[0] <= x0 - grow || f.box.min[0] >= x1 + grow) continue;
      if (f.box.max[1] <= y0 - grow || f.box.min[1] >= y1 + grow) continue;
      const top = f.box.max[2];
      // Only floors within the wall's own span: a floor three storeys up is
      // not this wall's ground.
      if (top < z0 - 0.05 || top > z1 - 1.0) continue;
      out.add(Math.round(top * 100) / 100);
    }
    return [...out].sort((a, b) => a - b);
  };

  for (const s of level.solids as Solid[]) {
    if (!WALLS.has(s.role)) continue;
    // Canonical (x, y, z) is Three (x, z, -y): the wall's thin axis in plan is
    // whichever of x or y is short.
    const cx0 = s.box.min[0];
    const cx1 = s.box.max[0];
    const cy0 = s.box.min[1];
    const cy1 = s.box.max[1];
    const zBot = s.box.min[2];
    const zTop = s.box.max[2];
    const w = cx1 - cx0;
    const d = cy1 - cy0;
    const h = zTop - zBot;
    if (h < 1.2) continue; // a sill or a lintel is already trim
    const alongX = w >= d;
    const run = alongX ? w : d;
    /*
     * Short enough to be a corner is not short enough to skip.
     *
     * The compiler emits the square where two runs cross as its own solid,
     * half to one wall thickness long — well under the metre this used to
     * demand — so the literal corner of every junction in the game was left
     * bare while both walls leading into it were banded. That is the gap you
     * see: the skirting stops, there is a bright block, and it starts again
     * at right angles. The guard is only here to keep trim off things that
     * are already trim, and the height test above does that job.
     */
    if (run < 0.08) continue;
    const thin = alongX ? d : w;
    // Three-space extents.
    const x0 = cx0;
    const x1 = cx1;
    const z0 = -cy1;
    const z1 = -cy0;

    /*
     * Base, head, and a bay joint. Nothing else, ever.
     *
     * Proud of the wall, which is a correction rather than a preference. This
     * used to inset the strips on every axis — the argument being that a
     * recessed reveal is a shadow and a proud one is only a moulding, which
     * is true — and the consequence was that every piece of trim in the game
     * was drawn a few centimetres *inside* a wall two hundred and twenty
     * millimetres thick. Entirely buried, on every wall, on every map. That is
     * most of why a twenty-metre interior wall in this game reads as a sheet
     * of paper: there was nothing on it, and there was never going to be,
     * because a recess is not something a box emitter can make in a solid it
     * does not own.
     *
     * What is available is a strip standing off the face, and what makes it
     * work is being big enough to see. Three centimetres wide at twenty
     * metres is a sub-pixel line; two hundred and forty is a pilaster with a
     * lit side, a shaded side and a shadow on the wall behind it.
     */
    const proud = Math.max(0.045, Math.min(0.07, thin * 0.3));
    /*
     * Nothing held back along the run, so the pieces actually meet.
     *
     * Two centimetres at each end was meant to stop corners crossing, and the
     * arithmetic never worked: the band projects up to seven centimetres off
     * the wall face, so a two-centimetre setback could not clear it either
     * way. Now that the junction squares are banded too, a segment and its
     * corner butt end to end and the run is continuous. The faces that touch
     * are back to back and inside the solid, which is nothing to look at.
     */
    const back = 0;

    /*
     * A shadow gap at the floor and another under the head.
     *
     * Graphite, thin, and unbroken. They are the horizontals of the
     * composition: they say where the wall starts and stops, and on a map
     * with no ceiling they are the only thing that does.
     */
    /*
     * One height for the whole map, not one per wall.
     *
     * These were a fraction of each solid's own height, clamped — so a run
     * against a tall room and the corner it meets against a low one produced
     * bands of different thickness butted together, stepping at the joint.
     * A skirting board is a stock length of material; it does not get thicker
     * because the ceiling is higher.
     *
     * Set at the top of the range the old formula could reach, not the middle
     * of it. The first attempt used the middle and the library went from 206
     * maps clean to 199: on a tall interior the clamp had been returning its
     * maximum, so a "consistent" band a third narrower took a third of the
     * dark off exactly the frames that were closest to failing for want of it,
     * and four warm interiors and three others fell over.
     */
    const skirt = night ? 0.38 : 0.24;
    /*
     * After dark the head band is a cove light rather than a shadow gap.
     *
     * Every map in the library that fails `masses` for having too little
     * *pale* in the frame is a night map, and a chlorine plant reads 0.3%
     * pale with half the frame a porcelain wall — the wall faces away from the
     * key and there is nothing in the picture that is its own light. No
     * arrangement of key and fill reaches it, which I measured; what a night
     * exterior needs is a lit thing in it.
     *
     * The band that says where the wall stops is already there and already
     * runs the length of every wall, so after dark it stops being the dark
     * you see into and becomes the light you see by. Taller then too, because
     * a shadow gap only has to be legible and a cove has to carry the light
     * mass of the frame.
     */
    const capBand = night ? 0.8 : 0.19;
    const head = night ? lamp : box;
    const headColour = night ? cove : reveal;
    /*
     * And the skirt is a light too, which is the increment that lands it.
     *
     * The cove and the pilasters took a night frame's pale mass from 0.3% to
     * 3.5%, and left four of these maps sitting at six and seven per cent
     * against the eight `masses` asks for. The band at the foot of the wall is
     * the last piece of trim there is and it is already drawn; lit, it is the
     * walkway edge every plant and platform in the world actually has.
     *
     * It costs the dark mass nothing worth having — these frames run 35% to
     * 62% dark and the skirt is a hand's width of it.
     */
    const foot = night ? lamp : box;
    const footColour = night ? cove : reveal;
    /*
     * One skirting per floor this wall stands beside.
     *
     * `zBot` is kept as a fallback for a wall the search finds no floor for —
     * a parapet, something over a void — because a wall with no skirting at
     * all is more obviously wrong than one with it in the usual place.
     */
    const grounds = levelsBeside(s);
    const feet = grounds.length ? grounds : [zBot];
    for (const g of feet) {
      if (g + skirt > zTop - capBand) continue;
      if (alongX) foot(x0 + back, g, z0 - proud, x1 - back, g + skirt, z1 + proud, footColour);
      else foot(x0 - proud, g, z0 + back, x1 + proud, g + skirt, z1 - back, footColour);
    }
    if (alongX) {
      head(x0 + back, zTop - capBand - 0.05, z0 - proud, x1 - back, zTop - 0.05, z1 + proud, headColour);
    } else {
      head(x0 - proud, zTop - capBand - 0.05, z0 + back, x1 + proud, zTop - 0.05, z1 - back, headColour);
    }

    // One division per bay, and a bay is wide: the point is to say how big the
    // wall is, which needs two or three joints across it and not fifteen.
    const bay = facade === 'industrial' ? 7.5 : facade === 'banded' ? 9 : 6;
    const bays = Math.floor(run / bay);
    if (bays >= 1 && h > 2.6) {
      for (let i = 1; i <= bays; i++) {
        const t = (i / (bays + 1)) * run;
        /*
         * The verticals are pale, not graphite.
         *
         * A dark stripe every six metres is a lot of dark on a wall that is
         * supposed to be the light part of the picture, and the horizontals
         * are already carrying the graphite. A pilaster in the shell colour
         * does the job the other way round: it catches the key on one face,
         * turns away from it on the other, and throws a soft edge onto the
         * wall behind — three values out of one piece of geometry, none of
         * them spending any of the palette.
         *
         * Measured, because the reasoning above is about a wall seen across a
         * room and the frames that fail `masses` for want of dark are ones
         * where the wall is the whole picture. Graphite pilasters do fix
         * those: bathhouse, school and siege all go clean, and bathhouse goes
         * from 7.5% dark to 12 views out of 12. They also break growhouse_c
         * and pier_b, and push archive_b and growhouse_b over the *other* edge
         * of the same gate — too much dark now. Whole library, both ways round
         * on the same seed: 206 of 255 pale, 207 graphite. One map, and two
         * maps made worse in the opposite direction.
         *
         * So the stripe is exactly as expensive as this comment said it was.
         * It is not that the wall-is-the-picture frames do not want more dark;
         * it is that a colour chosen at dressing time cannot know which frames
         * those are, and paying for three of them out of every wall in the
         * game is not a trade worth making for a net of one.
         */
        /*
         * And after dark the pilaster is a light too.
         *
         * The cove alone does not carry a night frame. It runs along the head
         * of a wall, and what fills the picture is the middle of the wall
         * below it: on a chlorine plant the coves took the pale mass from 0.3%
         * to 1.6% against the eight it needs. A vertical every bay reaches
         * down through the part of the wall that is actually in shot, and a
         * lit pilaster is the same piece of geometry doing the same job — it
         * says how big the wall is — by glowing instead of by catching the
         * key.
         */
        const half = night ? 0.2 : 0.12;
        const lo = zBot + skirt;
        const hi = zTop - capBand - 0.05;
        if (hi - lo < 0.5) continue;
        const draw = night ? lamp : box;
        const colour = night ? cove : pier;
        if (alongX) draw(x0 + t - half, lo, z0 - proud, x0 + t + half, hi, z1 + proud, colour);
        else draw(x0 - proud, lo, z1 - t - half, x1 + proud, hi, z1 - t + half, colour);
      }
    }
    trimmed++;
  }

  /*
   * And the ground gets a line on it, which nothing outdoors had.
   *
   * Hardstanding is one flat dark plane from wall to wall, and on the frames
   * that fail for being too dark it is two thirds of the picture with nothing
   * in it: a radar station reads 21.7% pale against 67.3% dark from a camera
   * looking across an empty apron. That is not a lighting problem and it is
   * not a per-map problem — it is that a real apron has paint on it and this
   * one does not.
   *
   * The same vocabulary as the walls: thin lines, sparse, saying what the
   * surface is. An edge line inset from the boundary and a division across
   * the middle of the long axis, and only on a slab big enough to be a yard
   * rather than a path — a line around a corridor floor is a corridor with a
   * stripe on it, which is the "shelving, everywhere, forever" mistake the
   * wall trim already learned once.
   *
   * It does not fix the frames it was built for, which I should say plainly.
   * The radar station has no hardstanding at all — its dark ground is the
   * interior floor — and extending the paint to interior slabs as well moved
   * that view not at all and the piers by two tenths of a point. Those frames
   * are dark because the material under them is dark and fills the picture,
   * not because it has nothing on it. What the paint is worth is that a yard
   * now reads as a yard: on a dizzy the lines run out across the plaza and
   * its worst view goes 27.2/60.8 to 28.2/59.7, which is a better picture and
   * still a failing one.
   */
  for (const s of level.solids as Solid[]) {
    if (s.role !== 'floor' || !marked(s)) continue;
    const w = s.box.max[0] - s.box.min[0];
    const d = s.box.max[1] - s.box.min[1];
    /*
     * A yard is wide enough to paint; a street is only wide enough to light.
     *
     * One threshold for both put the lamps where the paint goes, and the paint
     * wants a yard — seven metres across — while the ground that a dusk frame
     * is dark on is a road six metres wide with a slab either side of it. A
     * lunacy got eight lamps for the whole map and none of them in the frame
     * that fails.
     */
    const yard = Math.min(w, d) >= 7;
    const street = Math.max(w, d) >= 10 && Math.min(w, d) >= 3.5;
    if (!yard && !street) continue;
    const top = s.box.max[2];
    const lift = 0.015;
    const inset = Math.min(2.4, Math.max(0.9, Math.min(w, d) * 0.12));
    const t = 0.14;
    const x0 = s.box.min[0] + inset;
    const x1 = s.box.max[0] - inset;
    const z0 = -(s.box.max[1] - inset);
    const z1 = -(s.box.min[1] + inset);
    if (x1 - x0 < 1 || z1 - z0 < 1) continue;
    if (yard) {
    box(x0, top + lift, z0, x1, top + lift + 0.004, z0 + t, paint);
    box(x0, top + lift, z1 - t, x1, top + lift + 0.004, z1, paint);
    box(x0, top + lift, z0 + t, x0 + t, top + lift + 0.004, z1 - t, paint);
    box(x1 - t, top + lift, z0 + t, x1, top + lift + 0.004, z1 - t, paint);
    }
    /*
     * And a lamp in it, when the sky has gone.
     *
     * The frames that fail for being too dark at dusk fail for one reason:
     * the road is half the picture and a road at dusk is dark. Three things
     * that do not fix it, in order — the ground's own albedo up by half, paint
     * on the ground, and a lit kerb at the foot of the nearest wall — moved a
     * calavera nine tenths of a point between them, because a kerb is at the
     * foot of a wall and the middle of a road is nowhere near one.
     *
     * So the light goes where the dark is. A post, a head that is its own
     * light, and a pool of lit ground underneath it — which is the part that
     * matters, because it is the only one of the three with any area, and area
     * is what a road has too much of.
     *
     * The pool is a lit surface rather than a glowing plate: a fifth brighter
     * than the shell and unlit, so it reads as ground under a lamp and not as
     * a hole in the road with a light in it.
     */
    if (dim) {
      const along = Math.max(w, d);
      const lamps = Math.max(2, Math.min(6, Math.floor(along / 13)));
      const R = Math.min(3.4, Math.max(2.2, Math.min(w, d) * 0.22));
      for (let i = 0; i < lamps; i++) {
        const f = (i + 0.5) / lamps;
        // Down the middle, not along the edge. A row against one side lights
        // the side, and what is dark is the part of the road a camera looking
        // across it actually sees.
        const lx = w >= d ? x0 + (x1 - x0) * f : (x0 + x1) / 2;
        const lz = w >= d ? (z0 + z1) / 2 : z0 + (z1 - z0) * f;
        // The pool first, so the post stands in it rather than on it.
        lamp(lx - R, top + lift + 0.006, lz - R, lx + R, top + lift + 0.01, lz + R, pool);
        box(lx - 0.09, top, lz - 0.09, lx + 0.09, top + 4.2, lz + 0.09, post);
        lamp(lx - 0.26, top + 4.2, lz - 0.26, lx + 0.26, top + 4.46, lz + 0.26, lamplight);
      }
      trimmed++;
    }

    // One division across the long axis, so the yard has a size.
    if (yard && w >= d && x1 - x0 > 14) {
      const mx = (x0 + x1) / 2;
      box(mx - t / 2, top + lift, z0 + t, mx + t / 2, top + lift + 0.004, z1 - t, paint);
    } else if (yard && d > w && z1 - z0 > 14) {
      const mz = (z0 + z1) / 2;
      box(x0 + t, top + lift, mz - t / 2, x1 - t, top + lift + 0.004, mz + t / 2, paint);
    }
    trimmed++;
  }

  if (!trimmed) return group;

  // After dark every band is a fitting, so the graphite buffer comes out
  // empty and there is no shadow-gap mesh to add.
  if (idx.length) buildTrim();
  if (eIdx.length) buildCove();
  return group;

  function buildTrim(): void {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(norm, 3));
  geo.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geo.setIndex(idx);
  geo.computeBoundingSphere();
  const mesh = new THREE.Mesh(
    geo,
    new THREE.MeshStandardMaterial({
      // One material for both the graphite bands and the pale piers, so it
      // has to sit between the two: too metal and the piers are chrome, too
      // matte and the bands stop reading as the machined edge of a wall.
      vertexColors: true,
      roughness: 0.46,
      metalness: 0.22,
      envMapIntensity: 0.6,
    }),
  );
  mesh.name = 'dressing';
  group.add(mesh);
  }

  function buildCove(): void {
    const lit = new THREE.BufferGeometry();
    lit.setAttribute('position', new THREE.Float32BufferAttribute(ePos, 3));
    lit.setAttribute('normal', new THREE.Float32BufferAttribute(eNorm, 3));
    lit.setAttribute('color', new THREE.Float32BufferAttribute(eCol, 3));
    lit.setIndex(eIdx);
    lit.computeBoundingSphere();
    // Unlit and untone-mapped, the same as the scenery's signs: a basic
    // material shows the colour at full strength and ignores the scene's
    // lights, which is what a lit fitting at night actually does.
    const cove = new THREE.Mesh(
      lit,
      new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false }),
    );
    cove.name = 'dressing-cove';
    group.add(cove);
    void rand;
  }
}
